import React from "react";
import { Button, Card, CardContent } from "@lemonadetraining/web-ui";
import { IconCalendarEvent } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { formatCommitmentDate } from "@lemonadetraining/web-shared/utils/commitmentStatus";
import type { RenewalNotice } from "@lemonadetraining/web-shared/utils/commitmentStatus";
import { formatEuros } from "@lemonadetraining/web-shared/utils/commitmentPlans";
import {
  RENEWAL_CANCEL_AT_END,
  RENEWAL_EXPIRE_BODY,
  RENEWAL_EXPIRE_TITLE,
  RENEWAL_KEEP_GOING,
  RENEWAL_NOT_NOW,
  RENEWAL_RENEW,
  RENEWAL_ROLL_BODY,
  RENEWAL_ROLL_BODY_NO_PRICE,
  RENEWAL_ROLL_TITLE,
} from "./renewalCopy";
import { withPendingDate } from "../time/PendingDate";

interface RenewalNoticeCardProps {
  notice: Exclude<RenewalNotice, { kind: "none" }>;
  membershipName: string;
  subscriptionId: string;
  /** Expire only. Null means the gym has no storefront, so no Renew button. */
  renewHref: string | null;
  onDismiss: () => void;
  timeZone?: string | null;
  /** The gym's zone is still loading: the date's place is held, the actions still work. */
  datesPending?: boolean;
}

/**
 * The in-app half of the renewal notice: the same two facts the member was
 * pushed 30 days out, plus the decision itself.
 *
 * The heading is focusable but never focused on mount. Nothing just happened
 * here: the card is standing information for a month, and stealing focus every
 * time the memberships tab renders would fight the reader.
 */
const RenewalNoticeCard: React.FC<RenewalNoticeCardProps> = ({
  notice,
  membershipName,
  subscriptionId,
  renewHref,
  onDismiss,
  timeZone,
  datesPending,
}) => {
  const endDate = datesPending ? null : formatCommitmentDate(notice.endDate, timeZone);
  const isRoll = notice.kind === "roll";

  const title = withPendingDate(isRoll ? RENEWAL_ROLL_TITLE : RENEWAL_EXPIRE_TITLE, endDate);

  let bodyCopy: (date: string) => string;
  if (notice.kind === "roll") {
    const rollAmount = notice.rollMonthlyAmount;
    bodyCopy =
      rollAmount === null
        ? (date) => RENEWAL_ROLL_BODY_NO_PRICE(membershipName, date)
        : (date) => RENEWAL_ROLL_BODY(membershipName, formatEuros(rollAmount), date);
  } else {
    bodyCopy = (date) => RENEWAL_EXPIRE_BODY(membershipName, date);
  }
  const body = withPendingDate(bodyCopy, endDate);

  return (
    <Card data-testid="renewal-notice">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <IconCalendarEvent aria-hidden="true" className="size-5 text-foreground" />
          <h3
            tabIndex={-1}
            className="text-base font-semibold text-foreground outline-none"
            data-testid="renewal-notice-title"
          >
            {title}
          </h3>
        </div>
        <p className="text-sm text-foreground">{body}</p>
        <div className="flex flex-wrap items-center gap-2">
          {isRoll ? (
            <>
              <Button className="min-h-[44px] px-4" onClick={onDismiss} data-testid="renewal-keep">
                {RENEWAL_KEEP_GOING}
              </Button>
              <Button
                asChild
                variant="outline"
                className="min-h-[44px] px-4"
                data-testid="renewal-cancel-at-end"
              >
                <Link to={`/dashboard/membership/${subscriptionId}/cancel/confirm`}>
                  {RENEWAL_CANCEL_AT_END}
                </Link>
              </Button>
            </>
          ) : (
            <>
              {renewHref ? (
                <Button asChild className="min-h-[44px] px-4" data-testid="renewal-renew">
                  {/* The storefront lives on the marketing domain. Same tab: a
                      new window is a choice the member did not make. */}
                  <a href={renewHref}>{RENEWAL_RENEW}</a>
                </Button>
              ) : null}
              <Button
                variant="outline"
                className="min-h-[44px] px-4"
                onClick={onDismiss}
                data-testid="renewal-not-now"
              >
                {RENEWAL_NOT_NOW}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default RenewalNoticeCard;
